import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";

const eur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;

// Editor ordine cliente riutilizzabile (staff + portale responsabile)
export const OrdineForm = ({ prodotti, initial, onSubmit, onCancel, allowSaveNew = false }) => {
  const pmap = useMemo(() => Object.fromEntries(prodotti.map((p) => [p.id, p])), [prodotti]);
  const [nome, setNome] = useState(initial?.nome || "");
  const [cognome, setCognome] = useState(initial?.cognome || "");
  const [cellulare, setCellulare] = useState(initial?.cellulare || "");
  const [note, setNote] = useState(initial?.note || "");
  const [righe, setRighe] = useState(
    initial?.righe?.map((r) => ({ prodotto_id: r.prodotto_id, quantita: r.quantita, taglia: r.taglia || "", nome_retro: r.nome_retro || "" }))
    || [{ prodotto_id: prodotti[0]?.id || "", quantita: 1, taglia: "", nome_retro: "" }]
  );
  const [saving, setSaving] = useState(false);

  const setRiga = (i, k, v) => setRighe((rs) => rs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const addRiga = () => setRighe((rs) => [...rs, { prodotto_id: prodotti[0]?.id || "", quantita: 1, taglia: "", nome_retro: "" }]);
  const delRiga = (i) => setRighe((rs) => rs.filter((_, idx) => idx !== i));

  const totale = righe.reduce((s, r) => s + (pmap[r.prodotto_id]?.prezzo || 0) * (r.quantita || 0), 0);

  const build = () => {
    if (!nome.trim() || !cognome.trim()) { toast.error("Nome e cognome obbligatori"); return null; }
    if (cellulare.replace(/\D/g, "").length < 6) { toast.error("Cellulare non valido"); return null; }
    if (righe.length === 0) { toast.error("Aggiungi almeno un articolo"); return null; }
    for (const r of righe) {
      const p = pmap[r.prodotto_id];
      if (!p) { toast.error("Prodotto non valido"); return null; }
      if (p.richiede_taglia && !r.taglia) { toast.error(`Seleziona la taglia per ${p.nome_snapshot}`); return null; }
    }
    return {
      nome: nome.trim(), cognome: cognome.trim(), cellulare: cellulare.trim(), note,
      righe: righe.map((r) => ({ prodotto_id: r.prodotto_id, quantita: parseInt(r.quantita, 10) || 1, taglia: r.taglia || null, nome_retro: r.nome_retro || null })),
    };
  };

  const submit = async (andNew) => {
    const payload = build();
    if (!payload) return;
    setSaving(true);
    try {
      await onSubmit(payload, andNew);
      if (andNew) {
        setNome(""); setCognome(""); setCellulare(""); setNote("");
        setRighe([{ prodotto_id: prodotti[0]?.id || "", quantita: 1, taglia: "", nome_retro: "" }]);
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="ordine-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input data-testid="ordine-nome" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome*" className="bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100" />
        <input data-testid="ordine-cognome" value={cognome} onChange={(e) => setCognome(e.target.value)} placeholder="Cognome*" className="bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100" />
        <input data-testid="ordine-cellulare" value={cellulare} onChange={(e) => setCellulare(e.target.value)} placeholder="Cellulare*" inputMode="tel" className="bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100" />
      </div>

      <div className="space-y-3">
        {righe.map((r, i) => {
          const p = pmap[r.prodotto_id];
          return (
            <div key={i} data-testid={`riga-${i}`} className="bg-slate-900/40 rounded-xl p-3 space-y-2 border border-slate-800">
              <div className="flex gap-2">
                <select data-testid={`riga-prodotto-${i}`} value={r.prodotto_id} onChange={(e) => setRiga(i, "prodotto_id", e.target.value)} className="flex-1 bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100">
                  {prodotti.map((pp) => <option key={pp.id} value={pp.id}>{pp.nome_snapshot} — {eur(pp.prezzo)}</option>)}
                </select>
                <button type="button" onClick={() => delRiga(i)} className="px-2.5 rounded-lg bg-red-500/10 text-red-300 border border-red-500/30"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] uppercase text-slate-500">Quantità</label>
                  <input data-testid={`riga-qta-${i}`} type="number" min="1" value={r.quantita} onChange={(e) => setRiga(i, "quantita", e.target.value)} className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100" />
                </div>
                {p?.richiede_taglia && (
                  <div>
                    <label className="text-[10px] uppercase text-slate-500">Taglia*</label>
                    <select data-testid={`riga-taglia-${i}`} value={r.taglia} onChange={(e) => setRiga(i, "taglia", e.target.value)} className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100">
                      <option value="">—</option>
                      {(p.taglie_disponibili || []).map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                )}
                {p?.consente_nome_retro && (
                  <div className="col-span-2 sm:col-span-1">
                    <label className="text-[10px] uppercase text-slate-500">Nome retro</label>
                    <input data-testid={`riga-retro-${i}`} value={r.nome_retro} onChange={(e) => setRiga(i, "nome_retro", e.target.value.toUpperCase())} placeholder="Es. MARIO" className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 uppercase" />
                  </div>
                )}
              </div>
              <div className="text-right text-sm text-slate-400">Subtotale: <span className="font-mono-tech text-slate-200">{eur((p?.prezzo || 0) * (r.quantita || 0))}</span></div>
            </div>
          );
        })}
        <button type="button" data-testid="ordine-add-riga" onClick={addRiga} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-slate-700/40 text-slate-200 border border-slate-700 hover:bg-slate-700"><Plus className="w-4 h-4" /> Aggiungi articolo</button>
      </div>

      <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (opzionale)" rows={2} className="w-full bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2 text-slate-100 text-sm" />

      <div className="flex items-center justify-between border-t border-slate-800 pt-3">
        <span className="text-slate-400 text-sm">Totale ordine</span>
        <span data-testid="ordine-totale" className="text-2xl font-bold text-emerald-300">{eur(totale)}</span>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        {onCancel && <button type="button" onClick={onCancel} className="flex-1 py-3 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800">Annulla</button>}
        <button type="button" data-testid="ordine-salva" onClick={() => submit(false)} disabled={saving} className="flex-1 py-3 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salva ordine
        </button>
        {allowSaveNew && (
          <button type="button" data-testid="ordine-salva-nuovo" onClick={() => submit(true)} disabled={saving} className="flex-1 py-3 rounded-lg bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 font-semibold hover:bg-emerald-500/30 disabled:opacity-60">
            Salva e inserisci nuovo
          </button>
        )}
      </div>
    </div>
  );
};
